import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../../../firebase';

// Event Attendance interface matching the one in index.tsx
interface EventAttendance {
  eventId?: string;
  eventName: string;
  eventDate: string;
  eventDescription?: string;
  ticketType?: string;
  status?: string;
  attendeeId?: string;
  importDate: string;
  notes?: string;
}

interface EventAttendanceListProps {
  contactId: string;
  organizationId: string;
  events: EventAttendance[];
  onUpdate: () => void;
}

const EventAttendanceList: React.FC<EventAttendanceListProps> = ({
  contactId,
  organizationId,
  events,
  onUpdate
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [currentEvent, setCurrentEvent] = useState<EventAttendance>({
    eventName: '',
    eventDate: new Date().toISOString().split('T')[0],
    ticketType: 'General',
    status: 'Confirmed',
    importDate: new Date().toISOString()
  });

  // Format date helper
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Get current date in EST timezone
  const getCurrentESTDate = (): string => {
    const now = new Date();
    
    // Format in ISO with EST timezone indicator
    return now.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/(\d+)\/(\d+)\/(\d+),\s(\d+):(\d+):(\d+)/, '$3-$1-$2T$4:$5:$6-04:00');
  };

  const handleOpenDialog = (index?: number) => {
    if (index !== undefined && events[index]) {
      setCurrentEvent(events[index]);
      setEditIndex(index);
    } else {
      setCurrentEvent({
        eventName: '',
        eventDate: new Date().toISOString().split('T')[0],
        ticketType: 'General',
        status: 'Confirmed',
        importDate: getCurrentESTDate()
      });
      setEditIndex(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSaveEvent = async () => {
    if (!currentEvent.eventName || !currentEvent.eventDate) return;

    try {
      const contactRef = doc(db, `organizations/${organizationId}/contacts/${contactId}`);
      
      if (editIndex !== null) {
        // Update existing event attendance
        const newEvents = [...events];
        newEvents[editIndex] = currentEvent;
        
        await updateDoc(contactRef, {
          eventAttendance: newEvents
        });
      } else {
        // Add new event attendance
        await updateDoc(contactRef, {
          eventAttendance: arrayUnion(currentEvent)
        });
      }
      
      onUpdate();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving event attendance:', error);
    }
  };

  const handleDeleteEvent = async (index: number) => {
    try {
      const contactRef = doc(db, `organizations/${organizationId}/contacts/${contactId}`);
      const eventToRemove = events[index];
      
      await updateDoc(contactRef, {
        eventAttendance: arrayRemove(eventToRemove)
      });
      
      onUpdate();
    } catch (error) {
      console.error('Error removing event attendance:', error);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" component="div">
          Event Attendance
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Event
        </Button>
      </Box>
      
      {events.length > 0 ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Event Name</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Ticket Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Added</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <EventIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="body2">
                        {event.eventName}
                      </Typography>
                    </Box>
                    {event.eventDescription && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {event.eventDescription}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(event.eventDate)}</TableCell>
                  <TableCell>
                    {event.ticketType ? (
                      <Chip 
                        size="small" 
                        label={event.ticketType} 
                        color={
                          event.ticketType.toLowerCase().includes('vip') ? 'secondary' : 
                          event.ticketType.toLowerCase().includes('premium') ? 'primary' : 
                          'default'
                        }
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="small" 
                      label={event.status || 'Unknown'} 
                      color={
                        event.status?.toLowerCase().includes('confirm') ? 'success' :
                        event.status?.toLowerCase().includes('check') ? 'info' :
                        event.status?.toLowerCase().includes('cancel') ? 'error' :
                        'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {event.importDate ? formatDate(event.importDate) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(index)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeleteEvent(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box 
          p={3} 
          textAlign="center" 
          bgcolor="background.paper" 
          border={1} 
          borderColor="divider" 
          borderRadius={1}
        >
          <EventIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No event attendance records
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add event attendance records to track this contact's participation
          </Typography>
        </Box>
      )}
      
      {/* Add/Edit Event Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editIndex !== null ? 'Edit Event Attendance' : 'Add Event Attendance'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Name"
                value={currentEvent.eventName}
                onChange={(e) => setCurrentEvent({...currentEvent, eventName: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Event Date"
                  value={new Date(currentEvent.eventDate)}
                  onChange={(newValue) => {
                    if (newValue) {
                      setCurrentEvent({
                        ...currentEvent, 
                        eventDate: newValue.toISOString().split('T')[0]
                      });
                    }
                  }}
                  renderInput={params => (
                    <TextField {...params} size='small' fullWidth />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              
              <FormControl fullWidth>
                <InputLabel>Ticket Type</InputLabel>
                <Select
                  value={currentEvent.ticketType || 'General'}
                  label="Ticket Type"
                  onChange={(e) => setCurrentEvent({...currentEvent, ticketType: e.target.value})}
                >
                  <MenuItem value="General">General</MenuItem>
                  <MenuItem value="VIP">VIP</MenuItem>
                  <MenuItem value="Premium">Premium</MenuItem>
                  <MenuItem value="Early Bird">Early Bird</MenuItem>
                  <MenuItem value="Student">Student</MenuItem>
                  <MenuItem value="Speaker">Speaker</MenuItem>
                  <MenuItem value="Sponsor">Sponsor</MenuItem>
                  <MenuItem value="Staff">Staff</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={currentEvent.status || 'Confirmed'}
                  label="Status"
                  onChange={(e) => setCurrentEvent({...currentEvent, status: e.target.value})}
                >
                  <MenuItem value="Confirmed">Confirmed</MenuItem>
                  <MenuItem value="Checked In">Checked In</MenuItem>
                  <MenuItem value="Not Checked In">Not Checked In</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                  <MenuItem value="No Show">No Show</MenuItem>
                  <MenuItem value="Waitlisted">Waitlisted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Attendee ID"
                value={currentEvent.attendeeId || ''}
                onChange={(e) => setCurrentEvent({...currentEvent, attendeeId: e.target.value})}
                placeholder="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Description"
                value={currentEvent.eventDescription || ''}
                onChange={(e) => setCurrentEvent({...currentEvent, eventDescription: e.target.value})}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={currentEvent.notes || ''}
                onChange={(e) => setCurrentEvent({...currentEvent, notes: e.target.value})}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveEvent} 
            variant="contained" 
            disabled={!currentEvent.eventName || !currentEvent.eventDate}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventAttendanceList;
